const en = {
  tagline: "RSVP SPEAKING DRILL",
  coverBand: "English speaking · intensive drill",
  coverStamps: ["Your own script", "10 min a day", "¥980/mo unlimited"],
  tocTitle: "Contents",
  chapter: (n: number) => `Ch.${n}`,
  exampleLabel: "Example 1",
  exampleTask: "Keep your eyes between the red marks. Read each chunk aloud the moment it appears.",
  answerLabel: "Answer",
  heroTitle: "Eyes still.\nVoice moving.",
  heroDesc:
    "Rehearse the English you will actually say — your interview answer, your talk, your line in Monday's meeting. It appears one chunk at a time at a single point, in sync with native audio. Listen, say it back from memory, then read it aloud at the speaker's pace. No partner, no scoring, no prep.",
  ctaConnect: "Sign in with Google",
  ctaDocs: "See the contents",
  demoCaption: "Each chunk appears as the voice says it.",
  demoChunks: ["I'd like to", "check in", "for my flight", "to Denver,", "please."],

  problemKicker: "The problem",
  problemTitle: "You can read it. You just can't say it.",
  problemDesc:
    "You know every word, yet the sentence falls apart when you speak it: you pause in the wrong places and lose the rhythm. Reading on a page lets your eyes jump back and forth, so you never practice English in the order it is spoken.",

  modelKicker: "How it works",
  modelTitle: "One point. One chunk. One voice.",
  modeCols: ["Mode", "Audio", "What you do"],
  layer1: "Listen",
  layer1Audio: "On",
  layer1Desc:
    "Chunks appear at the center exactly as the native audio says them. Keep your eyes still and let the phrasing sink in.",
  layer2: "Echo",
  layer2Audio: "Pauses after each chunk",
  layer2Desc:
    "The audio pauses after each chunk, and the chunk disappears after a moment. Say it back from memory. With the voice check on, it moves on as soon as you finish.",
  layer3: "Read",
  layer3Audio: "Muted",
  layer3Desc:
    "The audio goes silent but the chunks keep the native timing. Read aloud at the speaker's pace.",
  layer4: "Finish",
  layer4Audio: "Muted, one step faster",
  layer4Desc: "One more silent pass, a little faster than the speaker. Finish the passage in one breath.",
  modelNote:
    "One click runs all four steps and moves on to the next passage. Paste your own script and it becomes a passage in about 30 seconds. Turn on the voice check and Perusor counts the chunks you actually said — it never records or uploads audio.",

  noteLabel: "Note",
  pointLabel: "Point",
  whyKicker: "Why RSVP",
  whyTitle: "Nothing to look back at.",
  why1Title: "Your own lines",
  why1Desc:
    "Skills are specific to what you practise. Rehearsing the exact sentences you will say brings the practice closest to the real moment.",
  why2Title: "Breaks and rhythm, in your body",
  why2Desc:
    "Chunks follow where a native speaker breathes and resets pitch. You practice those breaks every time you speak along.",
  whyResearch:
    "Studies have reported that reading-aloud support with a controlled presentation pace improved L2 oral fluency. Perusor applies this idea to speaking practice; we do not guarantee specific results.",

  pricingKicker: "Pricing",
  pricingTitle: "Start free. Unlock everything for ¥980 a month.",
  priceListTitle: "Price list",
  priceCols: ["Plan", "Includes", "Monthly"],
  freePlanName: "Free",
  freePlanDesc: "The free collection, the first passage of every collection, and one script of your own. No card required.",
  freePrice: "¥0",
  proPlanName: "Unlimited",
  proPlanDesc: "Every collection, as much as you like, plus 30 scripts of your own a month.",
  proStamp: "All collections",
  proPrice: "¥980",
  priceUnit: "/month, tax incl.",
  pricingTerms: [
    ["Renewal", "Renews automatically every month."],
    ["Cancel", "Anytime from Account (top right of the app). You keep access until the end of the current billing period."],
    ["Refunds", "No prorated refunds."],
  ],

  quotaTitle: "Today's quota",
  quotaItems: ["Listen to one passage", "Echo it back from memory", "Read it aloud with the timing", "Finish one step faster"],

  ctaFooterTitle: "Ten minutes a day, out loud.",
  ctaFooterDesc: "Sign in with Google and start with a free collection.",

  getStarted: "Get Started",
  dashboard: "Library",
  terms: "Terms",
  privacy: "Privacy Policy",
  copyright: "© 2026 Perusor. All rights reserved.",
};

const ja: typeof en = {
  tagline: "RSVP式 スピーキング特訓",
  coverBand: "英語スピーキング 集中特訓",
  coverStamps: ["自分の原稿で", "1日10分", "月980円 使い放題"],
  tocTitle: "目次",
  chapter: (n: number) => `第${n}章`,
  exampleLabel: "例題 1",
  exampleTask: "朱の目印の間から目を離さず、チャンクが出た瞬間に声に出して読め。",
  answerLabel: "解説",
  heroTitle: "目は一点、\n声は前へ。",
  heroDesc:
    "面接の自己紹介、発表原稿、会議で言う一言。本番で自分が言う英語を、意味のかたまり(チャンク)ごとに画面の一点へ、ネイティブ音声と同時に表示します。聴いて、見ずに後に続けて、最後は同じ速さで音読します。相手なし・採点なし・準備なし。",
  ctaConnect: "Google でサインイン",
  ctaDocs: "目次を見る",
  demoCaption: "チャンクは、声がそれを言う瞬間に表示されます。",
  demoChunks: ["I'd like to", "check in", "for my flight", "to Denver,", "please."],

  problemKicker: "課題",
  problemTitle: "読めるのに、口が回らない。",
  problemDesc:
    "単語は全部わかるのに、声に出すと文が崩れる。区切る位置を誤り、リズムを失う。紙の上の英文は目が行き来できるため、話される順番のまま英語を処理する練習になりません。",

  modelKicker: "仕組み",
  modelTitle: "一点に、ひとかたまり、ひとつの声。",
  modeCols: ["モード", "音声", "やること"],
  layer1: "Listen(聴く)",
  layer1Audio: "あり",
  layer1Desc:
    "ネイティブ音声がチャンクを言う瞬間に、そのチャンクが中央に出ます。目を動かさず、区切りとリズムを聴き取ります。",
  layer2: "Echo(後に続ける)",
  layer2Audio: "チャンクごとに停止",
  layer2Desc:
    "チャンクごとに音声が止まり、少しするとチャンクが消えます。見ずに記憶から言います。発声チェックが ON なら、言い終えた時点で次へ進みます。",
  layer3: "Read(一緒に読む)",
  layer3Audio: "消音",
  layer3Desc:
    "音声は消え、チャンクだけがネイティブと同じタイミングで進みます。そのペースで音読します。",
  layer4: "Finish(仕上げ)",
  layer4Audio: "消音・1 段速い",
  layer4Desc: "もう一度、ネイティブより少し速く無音で進みます。一息で読み切ります。",
  modelNote:
    "ワンクリックで 4 段階を自動で進み、次の本へ続きます。自分の原稿を貼ると約 30 秒で教材になります。発声チェックを ON にすると、声に出せたチャンクを数えます。録音も送信もしません。",

  noteLabel: "注",
  pointLabel: "ポイント",
  whyKicker: "なぜ RSVP か",
  whyTitle: "読み返す場所が、ない。",
  why1Title: "自分の台詞で練習する",
  why1Desc:
    "技能は練習した形に特化します。本番で言う文そのものを練習すると、練習が本番にいちばん近づきます。",
  why2Title: "区切りとリズムを体で覚える",
  why2Desc:
    "チャンクはネイティブが息継ぎし、抑揚を切り替える位置で分かれています。声を合わせるたびに、その区切りを練習します。",
  whyResearch:
    "提示ペースを制御した音読支援が、第二言語の口頭流暢性を改善したという研究報告があります。Perusor はこの考え方をスピーキング練習に応用したものであり、特定の効果を保証するものではありません。",

  pricingKicker: "料金",
  pricingTitle: "無料で始めて、月 980 円で全部。",
  priceListTitle: "受講料金表",
  priceCols: ["コース", "内容", "月額"],
  freePlanName: "無料",
  freePlanDesc: "無料コレクション、各コレクションの先頭 1 本、自分の原稿 1 本。カード登録は不要。",
  freePrice: "¥0",
  proPlanName: "使い放題",
  proPlanDesc: "全コレクションを好きなだけ。自分の原稿は月 30 本まで。",
  proStamp: "全コレクション",
  proPrice: "¥980",
  priceUnit: "/月(税込)",
  pricingTerms: [
    ["更新", "毎月自動更新。"],
    ["解約", "アプリ右上「アカウント」からいつでも解約可。解約後も現在の請求期間の末日まで利用可。"],
    ["返金", "日割り返金なし。"],
  ],

  quotaTitle: "今日のノルマ",
  quotaItems: ["Listen で 1 本聴く", "Echo で見ずに後に続ける", "Read で音読する", "Finish で少し速く読み切る"],

  ctaFooterTitle: "1 日 10 分、声に出す。",
  ctaFooterDesc: "Google でサインインして、無料コレクションから始めましょう。",

  getStarted: "はじめる",
  dashboard: "ライブラリ",
  terms: "利用規約",
  privacy: "プライバシーポリシー",
  copyright: "© 2026 Perusor. All rights reserved.",
};

export type Lang = "en" | "ja";
export type I18n = typeof en;

const translations: Record<Lang, I18n> = { en, ja };

export function t(lang: Lang): I18n {
  return translations[lang];
}

export function detectLang(pathname: string): Lang {
  return pathname.startsWith("/ja") ? "ja" : "en";
}
